import {useState} from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CheckIcon from '@mui/icons-material/Check';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import EditCaseButton from './EditCaseButton';
import FindCaseInput from './FindCaseInput';
import {formatCaseName} from '../../helpers/cases';
import {useUpdateSessionMutation} from '../../redux/services/sessionApi';

const FieldDisplay = ({label, value}) => (
  <Stack direction='row' spacing={2} marginBottom={1} alignItems='center'>
    <Typography variant='body' color='text.light'>
      {label}:
    </Typography>
    <Typography variant='body'>
      {value}
    </Typography>
  </Stack> 
);

const CaseEditor = ({
  caseData, 
  onCaseChange, 
  isLoading, 
  isSuccess, 
  patientId, 
  label,
  marginLeft,
  session,
}) => (
  <Box marginLeft={marginLeft}>
    <Stack direction='row' spacing={1}>
      <FindCaseInput
        hideLabel={true}
        label={label || null}
        defaultValue={caseData?.id}
        patientId={caseData?.patient_id || patientId} 
        minWidth={250}
        allowCreate={true}
        session={session}
        allowEdit={true}
        onSubmit={(e, {id}) => onCaseChange(e, {id})}
        includeNonOwned={true}
      />
      {isLoading && <CircularProgress size={20} />}
      {isSuccess && <CheckIcon color='success'/>}
    </Stack>
  </Box>
);

const fields = () => ([
  {
    label: 'Case',
    parser: (c, {session, onCaseChange, isLoading, isSuccess}) => (
      <CaseEditor
        caseData={c}
        onCaseChange={onCaseChange}
        isLoading={isLoading}
        isSuccess={isSuccess}
        marginLeft={7}
        session={session}
      />
    ),
},
  {
    label: 'Session Count',
    parser: c => c?.session_count || '',
  }
]);

const directBillTypes = ['AUTO', 'WORK_COMP', 'COMMERCIAL', 'LIEN', 'DIRECT_BILL'];

const getBillingInstructions = billingCategory => {
  if (directBillTypes.includes(billingCategory)) {
    return 'Please only bill for the allocated co-pay or co-insurance.';
  }
  switch (billingCategory) {
    case 'CASH':
      return 'Please charge the patient in full for the cost of care and they can utilize a superbill for reimbursement.';
    case 'COURTESY_BILL':
      return 'Please charge the patient the cost of care in full and we will submit for reimbursement on behalf of the patient.';
    default:
      return '';
  }
};

const handleCaseChange = ({sessionId, putSession}) => async (e, {id}) => {
  const req = await putSession({id: sessionId, body: {case_id: id}});
};

const BillingInfo = props => {
  return (
    <Box>
      <Stack spacing={0.75} marginTop={0.5}>
        <Typography variant='bodyLarge'>
          Billing Type:{' '}
          {
            directBillTypes.includes(props.case?.billing_category)
            ? 'Direct Bill'
            : props.case?.billing_category
          }
        </Typography>
        {props.showBillingInstructions && (
          <Typography variant='body' marginTop={4}>
            <strong>{getBillingInstructions(props.case?.billing_category)}</strong>
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const CaseSummary = props => {
  const {case: c, showBillingInstructions, session } = props;
  const [putSession, req] = useUpdateSessionMutation();
  const onCaseChange = handleCaseChange({
    sessionId: session.id, 
    putSession,
  });
  console.log('req', req);
  return (
    <Box>
      <Stack spacing={1}>
        {
          req.error &&
          <Alert color='error'>
            Error updating case on session.
          </Alert>
        }
        {c 
          ? fields().map(f => 
            <FieldDisplay 
              key={f.label} 
              label={f.label || null}
              value={f.parser(c, {session, onCaseChange, isLoading: req?.isLoading, isSuccess: req.isSuccess})}
            >
            </FieldDisplay>
          )
          : <Box sx={{width: '250px'}}>
              <CaseEditor
                caseData={null}
                label='Attach a Case'
                patientId={session.patient.id}
                onCaseChange={onCaseChange}
                isLoading={req?.isLoading}
                isSuccess={req.isSuccess}
                session={session}
            />
            </Box>
        }
        {
          session.category !== 'optimize' && c
          && <BillingInfo case={c} showBillingInstructions={showBillingInstructions}/>
        }
        {/* <Box>
          <EditCaseButton
            variant='text'
            size='small'
            editableFields={['name']}
            caseId={c.id}
            buttonText={'Edit Case'}
          />
        </Box> */}
      </Stack>
    </Box>
  );
};

CaseSummary.propTypes = {
  case: PropTypes.object,
  session: PropTypes.object,
  showBillingInstructions: PropTypes.bool,
};

export default CaseSummary;